import Phaser from 'phaser';
import {
  MAZE_MATCH,
  RACE_SIZES,
  ROUNDUP_MATCH,
  SOCCER_MATCH,
  type RaceSize,
} from '../../gen/modes';
import { haptics } from '../../platform/haptics';
import { shareLink } from '../../platform/share';
import { gameAudio } from '../audio/audio';
import { createRace, currentRace, joinRace, type RaceSession, type RaceView } from '../live';
import { lobbyCues, NOTHING_HEARD, type LobbyHeard } from '../lobbyCues';
import { isMatchMode, type RoomMode } from '../../meta/online/live';
import { PARTY_MODES } from '../../meta/online/duel';
import { MODE_WORDS } from '../modeWords';
import { drawModeTiles, MODE_TILES } from '../ui/modeTiles';
import { inviteToRoom } from '../hub';
import { myFriends, onlineOn } from '../online';
import type { FriendList, PlayerName } from '../../meta/online/schema';
import { saves } from '../records';
import { bevelButton, callAttention, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import type { RaceLevelData } from './RaceLevelScene';
import { wipeTo } from './WipeScene';
import { whoText } from '../nicknames';

/** RACE A FRIEND from VERSUS, or a race link (`?race=CODE`) a friend shared. */
export interface RaceData {
  join?: string;
  /** The player already said JOIN (to a friend's invite card): no asking again. */
  accepted?: boolean;
  /**
   * PLAY TOGETHER on a friend's card: INVITE opens the room and invites them straight away, and
   * BACK goes back to their card.
   */
  friend?: { player: string; name: PlayerName; back?: object };
  /**
   * The mode picked on CLUB > VERSUS (owner, 2026-10-29): straight to its setup. Without one (from a
   * friend's card), the mode tiles come first.
   */
  mode?: RoomMode;
}

const SIZE_NAMES: Record<RaceSize, { name: string; blurb: string }> = {
  quick: { name: 'QUICK', blurb: 'About a minute.' },
  normal: { name: 'NORMAL', blurb: 'About as long as a Daily.' },
  long: { name: 'LONG', blurb: 'The biggest lawns.' },
};

/** The link a friend taps to join (the room gives the same). */
const raceUrl = (code: string) => `https://playlawnmower.com/?race=${code}`;

/**
 * The live race's lobby (docs/LIVE.md, "How it plays"): pick a size and INVITE (a link through the
 * share sheet), then the room: who is in, ready, and START for the inviter. A friend arriving by
 * link is asked first ("wants a race!": JOIN or NOT NOW). When the room counts down, the race
 * itself starts (RaceLevelScene).
 */
export class RaceScene extends Phaser.Scene {
  private size: RaceSize = 'quick';
  /** A race on twin lawns, or head to head on one (docs/HEAD_TO_HEAD.md). Kept for the session. */
  private static mode: RoomMode = 'race';
  /** Two, or a party of up to four (docs/PARTY.md), in the modes that have one. */
  private static seats: 2 | 4 = 2;
  private session: RaceSession | null = null;
  private joining: string | null = null;
  /** A friend arriving by link has said JOIN. */
  private accepted = false;
  private busy = false;
  private note: string | null = null;
  private unsubscribe: (() => void) | null = null;
  /** What the host has been told already: the friend's honk, START's chime (docs/FRIENDS.md). */
  private heard: LobbyHeard = NOTHING_HEARD;
  /** START, while it can be pressed (it calls for attention every few seconds). */
  private startButton: { button: Phaser.GameObjects.Container; w: number; h: number } | null = null;
  /** The host's friends, to invite straight from the room (docs/FRIENDS.md); null until loaded. */
  private friends: FriendList['friends'] | null = null;
  /** Friends invited to this room: sent, or what went wrong. */
  private sent = new Map<string, string | null>();
  /** The friend PLAY TOGETHER came for (their card's), or null. */
  private invitee: RaceData['friend'] | null = null;
  /** Picking the mode first (from a friend's card), and whether it was picked here (BACK). */
  private picking = false;
  private pickedHere = false;

  constructor() {
    super('Race');
  }

  init(data: RaceData = {}): void {
    // Phaser starts a scene again with its last data when given none: a link's room is joined
    // once, not again from VERSUS (owner's build 22 report: the last friend's room kept coming back).
    this.sys.settings.data = {};
    this.joining = data.join ?? null;
    this.accepted = data.accepted ?? false;
    this.invitee = data.friend ?? null;
    if (data.mode) RaceScene.mode = data.mode;
    this.picking = !data.mode && !data.join && !!data.friend;
    this.pickedHere = false;
    this.busy = false;
    this.note = null;
    // A room this player opened and has not raced in yet is this one (back to it from VERSUS);
    // anything else (a race over, a friend's room joined from a link) is left, for a fresh start.
    const open = data.join ? null : currentRace();
    const view = open?.view;
    const reusable =
      !!view &&
      // (Another mode picked on VERSUS: a fresh room, not this one.)
      (!data.mode || view.mode === data.mode) &&
      view.line !== 'closed' &&
      !view.start &&
      !view.result &&
      !view.friendLeft &&
      (view.you === null || view.you === 0) &&
      (view.phase === null || view.phase === 'waiting');
    if (open && !reusable) open.leave();
    this.session = reusable ? open : null;
    // Back in a room already announced (from VERSUS): no second honk for the same friend.
    this.heard = view && reusable ? lobbyCues(NOTHING_HEARD, view).heard : NOTHING_HEARD;
    this.startButton = null;
    this.friends = null;
    this.sent = new Map();
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = null;
    });
    if (this.session) {
      this.watch(this.session);
      void this.loadFriends();
    }
    this.time.addEvent({ delay: 2600, loop: true, callback: () => this.nudgeStart() });
    this.draw();
    if (this.joining) void this.join(this.joining);
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    // The START button's attention-calling tweens go with the objects they move.
    this.tweens.killAll();
    this.startButton = null;
    clearScene(this);
    this.draw();
  };

  private watch(session: RaceSession): void {
    this.unsubscribe?.();
    this.session = session;
    this.unsubscribe = session.subscribe(this.onView);
  }

  private readonly onView = (view: RaceView): void => {
    // The countdown: off to the race (or the match).
    if (view.start && view.size) {
      this.scene.start(isMatchMode(view.mode) ? 'DuelLevel' : 'RaceLevel', {
        seed: view.start.seed,
        size: view.size,
        atLocal: view.start.atLocal,
      } satisfies RaceLevelData);
      return;
    }
    // The host hears a friend arrive (a honk), and START become ready (a chime).
    const cue = lobbyCues(this.heard, view);
    this.heard = cue.heard;
    if (cue.honk) {
      gameAudio.honk();
      haptics.impact();
    }
    if (cue.chime) {
      gameAudio.chime(false);
      haptics.tap();
    }
    this.redraw();
    if (cue.chime) this.nudgeStart();
  };

  /** START flashes and hops while it waits to be pressed. */
  private nudgeStart(): void {
    const start = this.startButton;
    if (!start || !start.button.active) return;
    callAttention(this, start.button, start.w, start.h, saves().save.settings.reducedShake);
  }

  private async join(code: string): Promise<void> {
    this.busy = true;
    const session = await joinRace(code);
    this.busy = false;
    if (typeof session === 'string') this.note = session;
    else {
      this.watch(session);
      // JOIN was said on the invite card: ready at once.
      if (this.accepted) session.ready();
    }
    this.redraw();
  }

  private async invite(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.note = null;
    this.redraw();
    const party = PARTY_MODES.includes(RaceScene.mode) && RaceScene.seats === 4;
    const made = await createRace(this.size, RaceScene.mode, party ? 4 : 2);
    this.busy = false;
    if (typeof made === 'string') {
      this.note = made;
      this.redraw();
      return;
    }
    this.watch(made.session);
    made.session.ready();
    this.redraw();
    // From a friend's card: straight to them. Otherwise with friends to pick from, the room lists
    // them; without, straight to the share sheet.
    const invitee = this.invitee;
    if (invitee) void this.inviteFriend(made.session.code, invitee.player);
    await this.loadFriends();
    if (!invitee && !this.friends?.length) await this.share(made.session.code);
  }

  /** The host's friends, online ones first (null offline). */
  private async loadFriends(): Promise<void> {
    const list = await myFriends();
    this.friends = list
      ? [...list.friends].sort((a, b) => Number(!!b.online) - Number(!!a.online))
      : [];
    this.redraw();
  }

  /** Invites a friend from the room's list. */
  private async inviteFriend(code: string, friend: string): Promise<void> {
    if (this.sent.get(friend) === null) return;
    this.sent.set(friend, null);
    this.redraw();
    const problem = await inviteToRoom(code, friend);
    if (problem) this.sent.set(friend, problem);
    else this.sent.set(friend, null);
    this.redraw();
  }

  private async share(code: string): Promise<void> {
    const size = SIZE_NAMES[this.session?.view.size ?? this.size].name.toLowerCase();
    const mode = this.session?.view.mode ?? RaceScene.mode;
    await shareLink('lawnmower.', MODE_WORDS[mode].share(size), raceUrl(code));
  }

  /** Android's back button: out of the lobby, leaving the room (as LEAVE does). */
  onBack(): void {
    this.leave();
  }

  private leave(): void {
    this.session?.leave();
    this.session = null;
    wipeTo(this, 'Versus');
  }

  // ── Drawing ────────────────────────────────────────────────────────────────────────────────

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    // The mode's name (picked, or the room's), else PLAY A FRIEND.
    const roomMode = this.session?.view.mode;
    const titled = roomMode ?? (this.joining || this.picking ? null : RaceScene.mode);
    const title = MODE_TILES.find((m) => m.mode === titled)?.name ?? 'PLAY A FRIEND';
    label(this, 6, 4, title, 2);
    const backY = height - 30;

    if (!onlineOn()) {
      this.lines(40, ['Online play is off.', 'Turn it on in Settings, Online.']);
      bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => wipeTo(this, 'Versus'));
      return;
    }
    const session = this.session;
    if (!session) {
      if (this.joining) {
        this.lines(40, [this.busy ? 'Finding the race...' : (this.note ?? '')]);
        bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => wipeTo(this, 'Versus'));
        return;
      }
      this.drawSetup(width, backY);
      return;
    }
    const view = session.view;
    if (view.line === 'closed') {
      this.lines(40, [view.closedWhy ?? 'This race is over.']);
      bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => this.leave());
      return;
    }
    if (view.you === null) {
      this.lines(40, ['Joining...']);
      bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => this.leave());
      return;
    }
    if (view.you !== 0 && !this.accepted) {
      this.drawInvited(view, width, backY);
      return;
    }
    this.drawRoom(session, view, width, backY);
  }

  /** Centered lines of text from `y`, wrapped. */
  private lines(y: number, texts: string[], color: number = UI.text): number {
    const { width } = this.scale.gameSize;
    for (const text of texts) {
      if (!text) continue;
      label(this, width / 2, y, text, 1, color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      y += wrapLineCount(text, width - 16) * 10 + 4;
    }
    return y;
  }

  private drawSetup(width: number, backY: number): void {
    const invitee = this.invitee;
    // From a friend's card: the mode first, as on VERSUS.
    if (this.picking) {
      const who = invitee ? whoText(invitee.player, invitee.name) : null;
      this.lines(30, [who ? `Play with ${who}: pick a game.` : 'Pick a game.'], UI.dim);
      drawModeTiles(this, 50, width, (mode) => {
        RaceScene.mode = mode;
        this.picking = false;
        this.pickedHere = true;
        this.redraw();
      });
      bevelButton(this, 6, backY, width - 12, 24, 'BACK', () =>
        invitee ? wipeTo(this, 'Friend', invitee.back) : wipeTo(this, 'Versus'),
      );
      return;
    }
    // The mode (named in the title): a line of what it is, then players, size and INVITE.
    const mode = RaceScene.mode;
    const soccer = SOCCER_MATCH[this.size];
    const roundup = ROUNDUP_MATCH[this.size];
    const maze = MAZE_MATCH[this.size];
    let y = this.lines(30, [MODE_TILES.find((m) => m.mode === mode)?.rules ?? '']);
    y += 6;
    // A party of up to four (docs/PARTY.md), in the modes that have one.
    if (PARTY_MODES.includes(mode)) {
      label(this, 6, y, 'PLAYERS', 1, UI.dim);
      const pw = Math.floor((width - 16) / 2);
      const pick = (seats: 2 | 4) => () => {
        RaceScene.seats = seats;
        this.redraw();
      };
      bevelButton(this, 6, y + 12, pw, 18, '2', pick(2), RaceScene.seats === 2);
      bevelButton(this, 10 + pw, y + 12, pw, 18, 'UP TO 4', pick(4), RaceScene.seats === 4);
      y += 36;
    }
    label(this, 6, y, 'SIZE', 1, UI.dim);
    y += 12;
    const w = Math.floor((width - 20) / 3);
    RACE_SIZES.forEach((size, i) =>
      bevelButton(
        this,
        6 + i * (w + 4),
        y,
        w,
        20,
        SIZE_NAMES[size].name,
        () => {
          this.size = size;
          this.redraw();
        },
        this.size === size,
      ),
    );
    y += 26;
    const blurb =
      mode === 'maze'
        ? `${maze.cells * 2 - 1} x ${maze.cells * 2 - 1} maze, ${maze.seconds} seconds.`
        : mode === 'soccer'
          ? `First to ${soccer.target}, ${soccer.seconds} seconds.`
          : mode === 'roundup'
            ? `First to ${roundup.target} of ${roundup.sheep}, ${roundup.seconds} seconds.`
            : SIZE_NAMES[this.size].blurb;
    label(this, width / 2, y, blurb, 1, UI.dim).setOrigin(0.5, 0);
    y += 18;
    bevelButton(
      this,
      6,
      y,
      width - 12,
      24,
      this.busy
        ? 'OPENING...'
        : this.invitee
          ? `INVITE ${whoText(this.invitee.player, this.invitee.name)}`
          : 'INVITE',
      () => void this.invite(),
      true,
    );
    y += 32;
    if (this.note) this.lines(y, [this.note], UI.accent);
    // BACK: to the mode tiles if picked here, else where this came from.
    bevelButton(this, 6, backY, width - 12, 24, 'BACK', () => {
      if (this.pickedHere) {
        this.picking = true;
        this.redraw();
      } else if (invitee) wipeTo(this, 'Friend', invitee.back);
      else wipeTo(this, 'Versus');
    });
  }

  /** A friend's link: who wants a race, on what size, and JOIN or NOT NOW. */
  private drawInvited(view: RaceView, width: number, backY: number): void {
    const host = view.players.find((p) => p.seat === 0);
    const g = this.add.graphics();
    navyPanel(g, 4, 34, width - 8, 64);
    label(
      this,
      width / 2,
      44,
      host ? whoText(host.player, host.name) : 'A friend',
      1,
      UI.accent,
    ).setOrigin(0.5, 0);
    label(this, width / 2, 56, MODE_WORDS[view.mode ?? 'race'].wants, 1, UI.text).setOrigin(0.5, 0);
    if (view.size) {
      label(this, width / 2, 74, `${SIZE_NAMES[view.size].name} lawn`, 1, UI.dim).setOrigin(0.5, 0);
    }
    bevelButton(
      this,
      6,
      108,
      width - 12,
      26,
      'JOIN',
      () => {
        this.accepted = true;
        haptics.tap();
        this.session?.ready();
        this.redraw();
      },
      true,
    );
    bevelButton(this, 6, backY, width - 12, 24, 'NOT NOW', () => this.leave());
  }

  private drawRoom(session: RaceSession, view: RaceView, width: number, backY: number): void {
    const g = this.add.graphics();
    label(this, width / 2, 30, `ROOM ${session.code}`, 1, UI.dim).setOrigin(0.5, 0);
    if (view.size) {
      const mode = view.mode ?? 'race';
      const what = mode === 'race' ? 'lawn' : MODE_WORDS[mode].name;
      label(this, width / 2, 40, `${SIZE_NAMES[view.size].name} ${what}`, 1, UI.dim).setOrigin(
        0.5,
        0,
      );
    }
    let y = 56;
    const seats = Math.max(2, view.seats);
    navyPanel(g, 4, y - 4, width - 8, seats * 14 + 8);
    for (let seat = 0; seat < seats; seat++) {
      const p = view.players.find((x) => x.seat === seat);
      const you = seat === view.you;
      const who = p ? `${whoText(p.player, p.name)}${you ? ' (you)' : ''}` : 'waiting...';
      label(this, 10, y, who, 1, p ? (you ? UI.accent : UI.text) : UI.dim);
      if (p) {
        const state = !p.here ? 'AWAY' : p.ready ? 'READY' : '';
        label(this, width - 10, y, state, 1, p.ready && p.here ? UI.good : UI.dim).setOrigin(1, 0);
      }
      y += 14;
    }
    y += 12;
    // Everyone in is ready: two in a room of two; in a party, two or more (soccer: two or four).
    const n = view.players.length;
    const enough =
      seats === 2
        ? n === 2
        : n >= 2 &&
          (n === 2 || PARTY_MODES.includes(view.mode ?? 'duel')) &&
          (view.mode !== 'soccer' || n % 2 === 0);
    const both = enough && view.players.every((p) => p.ready && p.here);
    // Soccer in a party: two or four (two teams of two).
    if (seats > 2 && view.mode === 'soccer' && n === 3 && view.you === 0) {
      this.lines(backY - 52, ['Soccer needs 2 or 4 players.'], UI.dim);
    }
    if (view.you === 0) {
      if (view.players.length < seats) {
        const friends = this.friends ?? [];
        if (friends.length > 0) {
          // Friends to invite straight from here (docs/FRIENDS.md), online first, as many as fit.
          label(this, 6, y - 6, seats > 2 ? 'INVITE FRIENDS' : 'INVITE A FRIEND', 1, UI.dim);
          y += 6;
          const last = backY - 32 - 30;
          for (const f of friends) {
            if (y + 16 > last) break;
            navyPanel(g, 4, y, width - 8, 16);
            if (f.online) g.fillStyle(UI.good).fillRect(9, y + 6, 4, 4);
            label(this, 16, y + 5, whoText(f.player, f.name), 1, UI.text);
            const state = this.sent.get(f.player);
            const text = state === undefined ? 'INVITE' : state === null ? 'SENT' : 'RETRY';
            bevelButton(this, width - 54, y + 2, 46, 12, text, () => {
              void this.inviteFriend(session.code, f.player);
            });
            y += 18;
          }
        } else {
          y = this.lines(
            y,
            [
              'Send the link to a friend.',
              `The ${MODE_WORDS[view.mode ?? 'race'].match} starts when you press START.`,
            ],
            UI.dim,
          );
        }
        bevelButton(
          this,
          6,
          y + 4,
          width - 12,
          20,
          'SHARE LINK',
          () => void this.share(session.code),
        );
        y += 30;
      }
      const start = bevelButton(
        this,
        6,
        backY - 32,
        width - 12,
        26,
        'START',
        () => session.start(),
        both,
      );
      if (both) this.startButton = { button: start, w: width - 12, h: 26 };
    } else {
      const host = view.players.find((p) => p.seat === 0);
      const text =
        host && !host.here
          ? ['Your friend stepped away.', "They'll be right back."]
          : [both ? 'Waiting for the host to start.' : 'Getting ready...'];
      this.lines(y, text, UI.dim);
    }
    if (view.problem) this.lines(y + 8, [view.problem], UI.accent);
    if (view.line === 'reconnecting') this.lines(backY - 48, ['Reconnecting...'], UI.dim);
    bevelButton(this, 6, backY, width - 12, 24, 'LEAVE', () => this.leave());
  }
}
